/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconProps } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ModalPresentationProps } from "@pacific/MobiusOverrides/Modal/Modal";
import color from "@pacific/XDL/Utilities/color";
import { css, keyframes } from "styled-components";

type ScannerStyles = {
    barcodeIcon: IconProps;
    barcodeButton: ButtonPresentationProps;
    mobileBarcodeModal: ModalPresentationProps;
    buttonsWrapper: InjectableCss;
};

const snapshotEffectKeyframes = keyframes`
    0% {
        max-height: 0;
        max-width: 0;
        top: 50%;
        left: 50%;
    }

    50% {
        max-height: 80%;
        max-width: 80%;
        top: 50%;
        left: 50%;
    }

    100% {
        max-width: 0;
        max-height: 0;
        top: 100%;
        left: 0;
    }
`;

const scannerStyles: ScannerStyles = {
    barcodeIcon: {
        size: 40,
    },
    barcodeButton: {
        css: css`
            background-color: ${color("common.background")};
        `,
    },
    mobileBarcodeModal: {
        cssOverrides: {
            modalBody: css`
                .viewoport-container {
                    position: relative;
                    display: flex;
                    justify-content: center;
                    max-height: 100%;

                    .detection {
                        position: absolute;
                        bottom: 0;
                        width: 100%;
                        height: 40px;
                        background-color: ${color("common.backgroundContrast", 0.5)};
                        z-index: 1;
                        display: flex;
                        justify-content: space-between;
                        padding: 0 10px;
                        align-items: center;

                        img {
                            position: relative;
                            bottom: 5px;
                            max-width: 100px;
                            max-height: 40px;
                            width: auto;
                            height: auto;
                            border: 2px solid ${getColor("common.white")};
                            box-shadow: 4px 4px 4px ${color("common.backgroundContrast", 0.25)};
                        }

                        .barcode-number {
                            display: inline-block;
                            margin: 0 10px;
                            line-height: 40px;
                            font-size: 18px;
                            letter-spacing: 1px;
                            font-weight: 500;
                            color: ${getColor("common.white")};
                        }
                    }
                }

                .viewport {
                    position: relative;
                    float: left;
                    display: inline-block;

                    video {
                        display: block;
                        width: 100%;
                        max-width: 100%;
                        max-height: 100%;
                        margin: auto;
                    }

                    .drawingBuffer {
                        position: absolute;
                        width: 100%;
                        height: 100%;
                        left: 0;
                        top: 0;
                    }

                    [clear="all"] {
                        display: none;
                    }

                    .screenshot-of-bar-code {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        border: 3px solid ${getColor("common.white")};
                        z-index: 2;
                        visibility: hidden;
                        width: 0;
                        height: 0;
                    }

                    .screenshot-of-bar-code.img-snapshot-effect {
                        animation-name: ${snapshotEffectKeyframes};
                        animation-duration: 0.6s;
                        animation-fill-mode: forwards;
                        width: auto;
                        height: auto;
                        visibility: visible;
                    }
                }
            `,
        },
        mergeCss: true,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 20px;
        `,
    },
};

export default scannerStyles;
